import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BookOpen, Search, ChevronRight, ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useFeatures } from '../contexts/FeaturesContext'
import { useOpCo } from '../context/OpCoContext'
import { READER_ROLE_ENABLED } from '../utils/readerAccess'
import { modules as allModules } from './guide/guideModules'
import {
  AUDIENCE_LABELS,
  getGuideAudience,
  previewableAudiences,
  visibleModules,
} from './guide/guideAudience'

// Update this date whenever guide content changes.
const GUIDE_LAST_UPDATED = '2026-11-02'

// Each person gets the guide for their level (Super Admin, Admin, User).
// Which modules that means is decided in guide/guideModules.js and guideAudience.js.
const DEFAULT_MODULE = 'getting-started'

// ---------------------------------------------------------------------------
// Guide Page
// ---------------------------------------------------------------------------
export default function Guide() {
  const { user } = useAuth()
  const features = useFeatures()
  const opco = useOpCo()
  const [searchParams] = useSearchParams()

  const ownAudience = getGuideAudience({
    user,
    opcoEnabled: opco.enabled,
    isOpcoAdmin: opco.isOpcoAdmin,
    readerEnabled: READER_ROLE_ENABLED,
  })
  // Admins can preview the levels below theirs, to see what their people see.
  const [viewAs, setViewAs] = useState(null)
  const audience = viewAs || ownAudience
  const previewing = audience !== ownAudience
  const previewOptions = previewableAudiences(ownAudience)

  const modules = useMemo(
    () => visibleModules(allModules, {
      audience,
      // Role gating only applies to the person's own level. When previewing a lower
      // level the role-limited modules (Approvals, Templates) stay visible.
      role: previewing ? undefined : user?.role,
      features,
      opcoEnabled: opco.enabled,
    }),
    [audience, previewing, user?.role, features, opco.enabled],
  )

  const requestedModule = searchParams.get('module')
  const [activeId, setActiveModule] = useState(
    allModules.some((m) => m.id === requestedModule) ? requestedModule : DEFAULT_MODULE,
  )
  // The open module may disappear when the level changes; fall back to the first one.
  const activeModule = modules.some((m) => m.id === activeId) ? activeId : (modules[0]?.id || DEFAULT_MODULE)
  const [search, setSearch] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const searchRef = useRef(null)

  const scrollToSection = (id, sectionTitle) => {
    // Match on the heading text first, so a guide whose list and numbers drift apart still lands right.
    const headings = Array.from(document.querySelectorAll('h3'))
    const byTitle = headings.find((h) => h.textContent.trim() === sectionTitle)
    if (byTitle) {
      byTitle.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const index = modules.find((m) => m.id === id)?.sections?.indexOf(sectionTitle)
    if (index !== undefined && index >= 0) {
      const target = Array.from(document.querySelectorAll('[data-section-number]'))
        .find((el) => el.dataset.sectionNumber === String(index + 1))
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Opening /guide?module=opco&section=Auto-assign... lands on that topic.
  useEffect(() => {
    const section = searchParams.get('section')
    if (requestedModule && section) setTimeout(() => scrollToSection(requestedModule, section), 150)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateTo = (id, sectionTitle) => {
    setActiveModule(id)
    setSearch('')
    setShowResults(false)

    if (sectionTitle) {
      // Wait for content to render, then scroll to the section
      setTimeout(() => scrollToSection(id, sectionTitle), 100)
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Build search results with ranking
  const getSearchResults = () => {
    if (!search.trim()) return []
    const q = search.toLowerCase()
    const results = []

    for (const mod of modules) {
      const Icon = mod.icon
      const labelMatch = mod.label.toLowerCase().includes(q)
      const keywordMatch = (mod.keywords || []).some(k => k.toLowerCase().includes(q))
      const matchedSections = mod.sections
        ? mod.sections.filter(s => s.toLowerCase().includes(q))
        : []

      if (labelMatch || keywordMatch || matchedSections.length > 0) {
        let score = 0
        if (mod.label.toLowerCase().startsWith(q)) score = 3
        else if (labelMatch) score = 2
        else score = 1

        results.push({ mod, Icon, matchedSections, score })
      }
    }

    return results.sort((a, b) => b.score - a.score)
  }

  const searchResults = getSearchResults()

  const renderContent = () => {
    const mod = modules.find((m) => m.id === activeModule)
    if (!mod) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-10 text-center">
          <BookOpen className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">There is no guide for this yet.</p>
        </div>
      )
    }
    const Content = mod.Component
    return <Content key={mod.id} onNavigate={navigateTo} />
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">How To Guide</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Step-by-step walkthroughs for the pages and features available to your role.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="guide-audience">
            <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2.5 py-0.5 text-xs font-semibold">
              {previewing ? 'Previewing' : 'Guide for'}: {AUDIENCE_LABELS[audience]}
            </span>
            {previewOptions.length > 1 && (
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" role="group" aria-label="View the guide as">
                {previewOptions.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setViewAs(a === ownAudience ? null : a)}
                    aria-pressed={a === audience}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${a === audience ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    {AUDIENCE_LABELS[a]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Last updated: {new Date(`${GUIDE_LAST_UPDATED}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
        <div className="relative" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search guides..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowResults(true) }}
            onFocus={() => search.trim() && setShowResults(true)}
            onKeyDown={(e) => e.key === 'Escape' && setShowResults(false)}
            className="pl-9 pr-4 py-2 w-72 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-200 bg-white"
          />
          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full mt-1 left-0 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="max-h-80 overflow-y-auto py-1">
                {searchResults.map(({ mod, Icon, matchedSections }) => (
                  <div key={mod.id}>
                    <button
                      onClick={() => navigateTo(mod.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 text-left transition-colors"
                    >
                      <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{mod.label}</span>
                    </button>
                    {matchedSections.length > 0 && (
                      <div className="ml-11 pb-1">
                        {matchedSections.slice(0, 3).map((sec) => (
                          <button
                            key={sec}
                            onClick={() => navigateTo(mod.id, sec)}
                            className="w-full text-left text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 px-2 py-1 rounded truncate"
                          >
                            {sec}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2">
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  {searchResults.length} module{searchResults.length !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>
          )}
          {showResults && search.trim() && searchResults.length === 0 && (
            <div className="absolute top-full mt-1 left-0 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 p-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">No results found</p>
            </div>
          )}
        </div>
      </div>

      {/* Two-column layout: collapsible sticky sidebar + content */}
      <div className={`grid grid-cols-1 ${sidebarCollapsed ? 'lg:grid-cols-[48px_1fr]' : 'lg:grid-cols-[240px_1fr]'} gap-6 transition-all`}>
        {/* Left: Module navigation (sticky + collapsible) */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <nav className={`space-y-1 ${sidebarCollapsed ? '' : ''}`}>
            {/* Collapse toggle */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-2"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
              {!sidebarCollapsed && <span>Collapse</span>}
            </button>

            {!sidebarCollapsed && (
              <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-2">Modules</p>
            )}
            {modules.map((mod) => {
              const Icon = mod.icon
              const isActive = activeModule === mod.id
              return (
                <button
                  key={mod.id}
                  onClick={() => navigateTo(mod.id)}
                  title={sidebarCollapsed ? mod.label : undefined}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-2.5 px-3'} py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">{mod.label}</span>
                      {isActive && <ChevronRight className="h-3.5 w-3.5 text-blue-500" />}
                    </>
                  )}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Right: Content area */}
        <div className="min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}
